import { TrendingUp, Brain, Heart, Clock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useCallback } from "react";

export const CaseStudies = () => {
  const navigate = useNavigate();

  const handleNavigation = useCallback((path: string) => {
    console.log('Attempting navigation to:', path);
    if (!path) {
      console.error('Navigation path is empty');
      return;
    }
    
    try {
      navigate(path);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  }, [navigate]);

  const impactCards = [
    {
      title: "Assessment Efficiency",
      metric: "40-50%",
      unit: "reduction",
      description: "in cognitive assessment time while maintaining clinical accuracy, based on AI automation studies in healthcare",
      icon: Clock,
      path: "/impact/assessment-efficiency",
      color: "blue"
    },
    {
      title: "Early Detection Rate",
      metric: "30-35%",
      unit: "improvement",
      description: "in early cognitive decline detection through continuous AI-powered monitoring and analysis",
      icon: Brain,
      path: "/impact/early-detection",
      color: "emerald"
    },
    {
      title: "Patient Satisfaction",
      metric: "25-30%",
      unit: "increase",
      description: "in patient satisfaction scores through personalized care and regular engagement",
      icon: Heart,
      path: "/impact/patient-satisfaction",
      color: "purple"
    },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-primary/10 mb-8">
            <TrendingUp className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Projected Impact
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Based on comprehensive industry research and healthcare studies, here's how Scienza Health can transform patient care
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {impactCards.map((impact, index) => (
            <Card 
              key={index} 
              className="p-8 hover:shadow-lg transition-all duration-300 flex flex-col relative overflow-hidden group cursor-pointer"
              onClick={() => handleNavigation(impact.path)}
            >
              <div className={`absolute inset-0 bg-${impact.color}-50/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              <div className={`bg-${impact.color}-50 p-4 rounded-2xl w-16 h-16 flex items-center justify-center mb-6`}>
                <impact.icon className={`w-8 h-8 text-${impact.color}-500`} />
              </div>
              <h3 className="text-xl font-semibold mb-4">{impact.title}</h3>
              <div className="mb-4">
                <span className={`text-4xl font-bold text-${impact.color}-600`}>{impact.metric}</span>
                <span className="text-gray-600 ml-2">{impact.unit}</span>
              </div>
              <p className="text-gray-600 mb-8 flex-grow">{impact.description}</p>
              <Button 
                variant="outline" 
                className="w-full mt-auto group-hover:bg-primary group-hover:text-white transition-colors duration-300"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNavigation(impact.path);
                }}
                type="button"
              >
                Learn More
              </Button>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};